import React, { useCallback } from 'react'
import {
  Box,
  Button,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  TextFieldProps,
} from '@mui/material'
import { Add, Delete } from '@mui/icons-material'
import { Field, FieldArray, useField } from 'formik'
import { useAppTranslations } from '../../utils/hooks/useAppTranslations'
import { labelStyle } from './TransformDisplayToEdition'
import { trimFieldOnBlur } from './trimFieldOnBlur'
import { compactFormFieldSx } from './DisplayedItem'

export interface InputFieldArrayProps {
  name: string
  label: string
  type: 'text' | 'number' | 'email' | 'password' | 'url'
  autoFocus?: boolean
  fullWidth?: boolean
  margin?: TextFieldProps['margin']
  defaultHelperText?: (name: string) => void | string
  parentDefaultHelperText?: string
  required?: boolean
  InputLabelProps?: TextFieldProps['InputLabelProps']
  onMouseLeave?: () => void
  variant?: 'filled' | 'outlined' | 'standard'
  addButtonLabel?: string
  canAdd?: boolean
  hideLabel?: boolean
  showErrorOnInit?: boolean
  compact?: boolean
}

export interface SubInputFieldArrayProps {
  name: string
  label: string
  type: string
  autoFocus?: boolean
  fullWidth?: boolean
  margin?: TextFieldProps['margin']
  defaultHelperText?: (name: string) => void | string
  required?: boolean
  InputLabelProps?: TextFieldProps['InputLabelProps']
  variant?: 'filled' | 'outlined' | 'standard'
  onRemove?: () => void
  showErrorOnInit?: boolean
  compact?: boolean
}

const SubInputField: React.FC<SubInputFieldArrayProps> = ({
  name,
  label,
  type,
  defaultHelperText,
  margin = 'normal',
  variant = 'standard',
  onRemove,
  showErrorOnInit = false,
  compact = false,
  InputLabelProps,
  ...props
}) => {
  const t = useAppTranslations()
  const [field, meta, helpers] = useField<string>({ name })

  const hasError = Boolean(meta.error && (meta.touched || showErrorOnInit))
  const helperTextToDisplay = typeof defaultHelperText === 'function' ? defaultHelperText(name) : defaultHelperText
  const helperText = (meta.error && hasError && t(meta.error)) || helperTextToDisplay

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => trimFieldOnBlur(field, helpers)(event),
    [field, helpers]
  )

  return (
    <Field
      as={TextField}
      id={name}
      type={type}
      hiddenLabel={compact}
      label={t(label)}
      margin={compact ? 'none' : margin}
      helperText={helperText}
      error={hasError}
      variant={variant}
      size="small"
      fullWidth
      sx={compact ? compactFormFieldSx : undefined}
      name={field.name}
      value={field.value ?? ''}
      onChange={field.onChange}
      onBlur={handleBlur}
      slotProps={
        onRemove
          ? {
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton aria-label="delete item" onClick={onRemove} edge="end" size="large">
                      <Delete />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }
          : undefined
      }
      {...props}
    />
  )
}

const InputFieldArray: React.FC<InputFieldArrayProps> = ({
  name,
  label,
  type,
  defaultHelperText,
  parentDefaultHelperText,
  margin = 'normal',
  onMouseLeave,
  variant = 'standard',
  addButtonLabel = 'commons.actions.add',
  canAdd = true,
  hideLabel = false,
  showErrorOnInit = false,
  compact = false,
  InputLabelProps,
  ...props
}) => {
  const t = useAppTranslations()
  const [, meta] = useField({ name })

  const hasError = Boolean(meta.error && (meta.touched || showErrorOnInit))

  return (
    <Box onMouseLeave={onMouseLeave}>
      <FieldArray
        name={name}
        render={(arrayHelpers: any) => (
          <Box>
            <Grid container>
              {!hideLabel && !compact && (
                <Grid
                  size={12}
                  sx={{
                    mt: '9px',
                  }}
                >
                  <FormLabel sx={{ ...labelStyle }}>{t(label)}</FormLabel>
                </Grid>
              )}
              <Grid size={12}>
                {meta.value &&
                  meta.value.length > 0 &&
                  meta.value.map((_item: unknown, index: number) => (
                    <SubInputField
                      key={index}
                      type={type}
                      label={`${t(label)} ${index + 1}`}
                      margin={margin}
                      defaultHelperText={defaultHelperText}
                      variant={variant}
                      fullWidth
                      name={`${name}.${index}`}
                      showErrorOnInit={showErrorOnInit}
                      compact={compact}
                      onRemove={
                        index === 0 && props.required && meta.value.length < 2
                          ? undefined
                          : () => arrayHelpers.remove(index)
                      }
                      {...props}
                    />
                  ))}
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  onClick={() => arrayHelpers.push('')}
                  startIcon={<Add />}
                  disabled={!canAdd}
                >
                  {t(addButtonLabel)}
                </Button>
              </Grid>
            </Grid>
            <FormHelperText error={hasError}>{parentDefaultHelperText}</FormHelperText>
          </Box>
        )}
      />
    </Box>
  )
}

export default InputFieldArray
